import DefaultLayout from '@/components/layout/DefaultLayout.tsx';
import {
    COOKIE_USER_TOKEN,
    DEFAULT_MODEL_ID,
    SESSION_AI_CHAT_AUTO_SUBMIT,
    SESSION_AI_CHAT_INPUT,
} from '@/scripts/const.ts';
import { Button } from 'antd-mobile';
import { useEffect, useRef, useState } from 'react';
import useAjax from '@/hooks/useAjax.ts';
import AiChatBubble from '@/pages/ai-chat/components/AiChatBubble.tsx';
import { CHAT_ROLE } from '@/scripts/enum.ts';
import Scrollbars from 'react-custom-scrollbars-2';
import _ from 'lodash';
import { randomNumberId } from '@/scripts/utils.tsx';
import Cookies from 'js-cookie';
import { useTitle } from 'ahooks';
import AiChatFooter from '@/pages/ai-chat/components/AiChatFooter.tsx';
import { useSearchParams } from 'react-router-dom';
import { IChatModelDetail, IChatSessionBubble, IChatSessionData } from '@/types/.fetch.ts';

const PAGE_SIZE = 20;
let controller = new AbortController();

export default function AiChat() {
    useTitle('Ai智答');
    const [search] = useSearchParams();
    const querySessionId = search.get('sessionId');

    const scrollArea = useRef<Scrollbars>(null);
    const inputRef = useRef<any>(null);
    const pendingBubbleId = useRef<number>();
    const [chatHistory, setChatHistory] = useState<IChatSessionBubble[]>([]);
    const [hasLoadAllHistory, setHasLoadAllHistory] = useState(false);
    const [streamPending, setStreamPending] = useState(false);

    useEffect(() => {
        const prevInput = sessionStorage.getItem(SESSION_AI_CHAT_INPUT);
        const autoSubmit = sessionStorage.getItem(SESSION_AI_CHAT_AUTO_SUBMIT) === 'true';
        if (prevInput) {
            if (autoSubmit) {
                handleSubmit(prevInput);
                sessionStorage.setItem(SESSION_AI_CHAT_AUTO_SUBMIT, 'false');
            } else {
                inputRef?.current?.setInputValue(prevInput);
            }
        }
    }, []);

    // 从历史会话中带参数跳转回来时调用
    useEffect(() => {
        if (querySessionId) {
            getAnswer({ sessionId: querySessionId }).then((resp) => {
                setChatHistory(resp);
                setTimeout(() => {
                    scrollArea.current?.scrollToBottom();
                }, 500);
            });
        }
    }, [querySessionId]);

    // 获取引导信息
    const { response: guideData } = useAjax<IChatModelDetail>({
        url: `/app/ai/model?id=${DEFAULT_MODEL_ID}`,
    });

    // 创建会话
    const { fetch: createSession, response: sessionData } = useAjax<IChatSessionData>(
        {
            url: '/app/ai/session',
            data: {
                modelId: DEFAULT_MODEL_ID,
            },
            method: 'POST',
        },
        {
            manual: true,
        },
    );

    // 获取对话历史
    const { fetch: getAnswer, isLoading: isGetAnswerLoading } = useAjax<IChatSessionBubble[]>(
        {
            url: `/app/ai/chat/list?size=${PAGE_SIZE}&ascFlag=false&orderColumns=chatId`,
        },
        {
            manual: true,
            format: (res) => {
                return res.data.data.reverse();
            },
        },
    );

    const stopGen = () => {
        controller.abort('user stop gen');
        setStreamPending(false);
        pendingBubbleId.current = undefined;
        controller = new AbortController();
    };

    const handleSubmit = _.throttle(async (question: string) => {
        sessionStorage.setItem(SESSION_AI_CHAT_INPUT, question);
        inputRef.current?.inputBlur();
        if (question && !_.isEmpty(question) && !streamPending) {
            // 从URL获取旧会话信息，或者读取新会话的信息
            let tempSession = querySessionId ? { sessionId: +querySessionId, modelId: DEFAULT_MODEL_ID } : sessionData;
            // 需要临时创建新会话
            if (!sessionData && !querySessionId) {
                tempSession = await createSession({ title: question.substring(0, 20) });
            }
            if (!!tempSession) {
                pendingBubbleId.current = randomNumberId();
                const chatHistoryClone: IChatSessionBubble[] = [
                    ...chatHistory,
                    {
                        chatId: randomNumberId(),
                        content: question,
                        roleType: CHAT_ROLE['用户'],
                        modelId: tempSession.modelId,
                        sessionId: tempSession.sessionId,
                    },
                    {
                        chatId: pendingBubbleId.current,
                        content: '',
                        roleType: CHAT_ROLE['助手'],
                        modelId: tempSession.modelId,
                        sessionId: tempSession.sessionId,
                    },
                ];
                setChatHistory(chatHistoryClone);
                inputRef?.current?.setInputValue('');
                setStreamPending(true);
                fetch(`${import.meta.env.VITE_API}/app/ai/chat/ask`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        token: Cookies.get(COOKIE_USER_TOKEN) as string,
                    },
                    body: JSON.stringify({
                        modelId: DEFAULT_MODEL_ID,
                        roleType: CHAT_ROLE['用户'],
                        content: question,
                        sessionId: tempSession.sessionId,
                    }),
                    signal: controller.signal,
                })
                    .then(async (res) => {
                        const reader = res.body?.getReader();
                        const textDecoder = new TextDecoder();
                        while (true) {
                            if (reader) {
                                const { done, value } = await reader.read();
                                if (done) {
                                    stopGen();
                                    return;
                                }
                                const str = textDecoder.decode(value);
                                const pendingBubble = chatHistoryClone.find(
                                    (item) => item.chatId === pendingBubbleId.current,
                                );
                                if (pendingBubble) {
                                    pendingBubble.content += str;
                                }
                                setChatHistory([...chatHistoryClone]);
                                scrollArea.current?.scrollToBottom();
                            }
                        }
                    })
                    .finally(() => {
                        sessionStorage.removeItem(SESSION_AI_CHAT_INPUT);
                        stopGen();
                    });
            }
        }
    }, 1000);

    // 下拉加载更多历史消息
    const handleLoadHistory = _.throttle(async () => {
        if (!isGetAnswerLoading && !hasLoadAllHistory) {
            const lastId = chatHistory.length > 0 ? chatHistory[0].chatId : undefined;
            const prevHistory = await getAnswer({ lastId, sessionId: querySessionId });
            setChatHistory([...prevHistory, ...chatHistory]);
            if (prevHistory?.length <= 0) {
                setHasLoadAllHistory(true);
            }
        }
    }, 2000);

    return (
        <DefaultLayout
            className="bg-white bg-cover bg-no-repeat"
            style={{ backgroundImage: 'url(/assets/images/bg-aichat.jpg)' }}
        >
            <Scrollbars className="h-full scroll-smooth" ref={scrollArea}>
                <div className="flex w-full flex-col gap-7 px-4 py-5">
                    {!hasLoadAllHistory && querySessionId && chatHistory.length >= 20 ? (
                        <Button size="small" className="border-none bg-white/30" onClick={handleLoadHistory}>
                            加载更多历史对话
                        </Button>
                    ) : null}
                    {chatHistory.map((item, index) => {
                        const isUser = item.roleType === CHAT_ROLE['用户'];
                        const avatar = isUser ? undefined : `/assets/images/icon_aichat_${guideData?.modelAvatar}.jpg`;
                        return (
                            <AiChatBubble
                                avatar={avatar}
                                right={isUser}
                                key={index}
                                pending={index === chatHistory.length - 1 && streamPending}
                                onStop={stopGen}
                            >
                                {item.content}
                            </AiChatBubble>
                        );
                    })}
                    <div className="h-14 w-full flex-shrink-0"></div>
                </div>
            </Scrollbars>
            <AiChatFooter onSubmit={handleSubmit} ref={inputRef} />
        </DefaultLayout>
    );
}
